import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAppSettings } from '../stores/appSettings';
import { useTelegram } from '../hooks/useTelegram';
import { getBillingStatus } from '../utils/api';
import type { SubscriptionPlan } from '@tg-miniapp/shared-types';

export function DashboardPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { initData } = useTelegram();
  const { currentRole } = useAppSettings();
  const [subStatus, setSubStatus] = useState<{ plan: SubscriptionPlan, expiresAt: number | null } | null>(null);

  useEffect(() => {
    if (currentRole === 'owner' && initData) {
      getBillingStatus(initData).then(res => {
        if (res.ok) setSubStatus(res.value);
      });
    }
  }, [currentRole, initData]);

  // 1. --- ЭКРАН СУПЕР-АДМИНА (Метрики платформы) ---
  if (currentRole === 'admin') {
    return (
      <div className="animate-in fade-in">
        <h1 className="text-2xl font-bold mb-6">Метрики платформы 🛡️</h1>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-tg-theme-bg p-4 rounded-2xl border border-black/5">
            <div className="text-[10px] uppercase font-bold text-tg-theme-hint mb-1">MRR</div>
            <div className="text-xl font-bold">142,500 ₽</div>
            <div className="text-[10px] text-green-500">↑ 12%</div>
          </div>
          <div className="bg-tg-theme-bg p-4 rounded-2xl border border-black/5">
            <div className="text-[10px] uppercase font-bold text-tg-theme-hint mb-1">Churn</div>
            <div className="text-xl font-bold text-red-500">2.4%</div>
            <div className="text-[10px] text-tg-theme-hint">Стабильно</div>
          </div>
          <div className="bg-tg-theme-bg p-4 rounded-2xl border border-black/5">
            <div className="text-[10px] uppercase font-bold text-tg-theme-hint mb-1">Конверсия</div>
            <div className="text-xl font-bold">18.5%</div>
            <div className="text-[10px] text-tg-theme-hint">Free → PRO</div>
          </div>
          <div className="bg-tg-theme-bg p-4 rounded-2xl border border-black/5">
            <div className="text-[10px] uppercase font-bold text-tg-theme-hint mb-1">Боты</div>
            <div className="text-xl font-bold">156</div>
            <div className="text-[10px] text-green-500">+12 за неделю</div>
          </div>
        </div>

        <h2 className="text-lg font-bold mb-3">Активность</h2>
        <div className="bg-tg-theme-bg p-4 rounded-2xl border border-black/5 h-32 flex items-end gap-2">
          {[40, 60, 45, 80, 95, 70, 100].map((h, i) => (
            <div key={i} className="flex-1 bg-blue-500 rounded-t-lg" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    );
  }

  // 2. --- ЭКРАН МАСТЕРА / СОТРУДНИКА (Мой график) ---
  if (currentRole === 'master' || currentRole === 'assistant') {
    return (
      <div className="animate-in fade-in">
        <h1 className="text-2xl font-bold mb-6">Мой день ✂️</h1>
        <div className="bg-blue-500 text-white p-6 rounded-3xl shadow-lg mb-6 shadow-blue-500/20">
          <div className="text-xs opacity-80 uppercase font-bold tracking-widest mb-1">Заработок сегодня</div>
          <div className="text-3xl font-bold">4,200 ₽</div>
          <div className="text-xs mt-2 bg-white/20 inline-block px-2 py-1 rounded-lg">8 услуг выполнено</div>
        </div>

        <h2 className="text-lg font-bold mb-3">Ближайшие записи</h2>
        <div className="space-y-3">
          <div className="bg-tg-theme-bg p-4 rounded-2xl border border-black/5 flex justify-between items-center">
            <div>
              <div className="font-bold">Анна Иванова</div>
              <div className="text-xs text-tg-theme-hint">Стрижка + Окрашивание</div>
            </div>
            <div className="bg-black/5 px-3 py-1 rounded-lg font-bold">14:00</div>
          </div>
          <div className="bg-tg-theme-bg p-4 rounded-2xl border border-black/5 flex justify-between items-center">
            <div>
              <div className="font-bold">Мария Петрова</div>
              <div className="text-xs text-tg-theme-hint">Маникюр</div>
            </div>
            <div className="bg-black/5 px-3 py-1 rounded-lg font-bold">16:30</div>
          </div>
        </div>
      </div>
    );
  }

  // 3. --- ЭКРАН ВЛАДЕЛЬЦА БИЗНЕСА (Аналитика доходов) ---
  return (
    <div className="animate-in fade-in pb-10">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold">Панель управления 📈</h1>
          <p className="text-xs text-tg-theme-hint">Обзор вашего бизнеса</p>
        </div>
        {subStatus && (
          <div 
            onClick={() => navigate('/billing')}
            className={`px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-wider cursor-pointer transition-transform active:scale-95 ${
              subStatus.plan === 'PRO' ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30' :
              subStatus.plan === 'START' ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30' :
              'bg-black/10 dark:bg-white/10 text-tg-theme-text'
            }`}
          >
            Тариф: {subStatus.plan} {subStatus.plan === 'FREE' ? '🎁' : '💎'}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="bg-tg-theme-bg p-5 rounded-3xl shadow-sm border border-black/5">
          <div className="text-[10px] uppercase font-bold text-tg-theme-hint mb-1">Сегодня</div>
          <div className="text-xl font-bold">12,400 ₽</div>
          <div className="text-[10px] text-green-500 font-bold">+15%</div>
        </div>
        <div className="bg-tg-theme-bg p-5 rounded-3xl shadow-sm border border-black/5">
          <div className="text-[10px] uppercase font-bold text-tg-theme-hint mb-1">За месяц</div>
          <div className="text-xl font-bold">342,000 ₽</div>
          <div className="text-[10px] text-tg-theme-hint">К цели: 85%</div>
        </div>
      </div>

      <h2 className="text-lg font-bold mb-4">🏆 Топ мастеров</h2>
      <div className="space-y-3 mb-8">
        <div className="bg-tg-theme-bg p-4 rounded-2xl border border-black/5 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center font-bold text-blue-500">Д</div>
            <div>
              <div className="font-bold">Дмитрий С.</div>
              <div className="text-[10px] text-tg-theme-hint">84 записи • 4.9 ⭐</div>
            </div>
          </div>
          <div className="font-bold">124к</div>
        </div>
        <div className="bg-tg-theme-bg p-4 rounded-2xl border border-black/5 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/10 rounded-full flex items-center justify-center font-bold text-orange-500">Е</div>
            <div>
              <div className="font-bold">Елена В.</div>
              <div className="text-[10px] text-tg-theme-hint">62 записи • 5.0 ⭐</div>
            </div>
          </div>
          <div className="font-bold">98к</div>
        </div>
      </div>

      <h2 className="text-lg font-bold mb-4">⚡ Быстрые действия</h2>
      <div className="grid grid-cols-2 gap-3">
        <button 
          onClick={() => navigate('/bookings')}
          className="bg-black/5 dark:bg-white/5 p-4 rounded-2xl text-sm font-bold active:scale-95 transition-transform flex flex-col items-center gap-2"
        >
          <span>📅</span> Записи
        </button>
        <button 
          onClick={() => navigate('/staff')}
          className="bg-black/5 dark:bg-white/5 p-4 rounded-2xl text-sm font-bold active:scale-95 transition-transform flex flex-col items-center gap-2"
        >
          <span>👥</span> Команда
        </button>
      </div>
    </div>
  );
}
